import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useFirestore } from '../hooks/useFirestore';
import EarningsChart from '../components/Earnings/EarningsChart';
import Breadcrumbs from '../components/Navigation/Breadcrumbs';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCoins, FiCalendar, FiTrendingUp, FiFileText, FiFilter } = FiIcons;

const Earnings = () => {
  const [timeRange, setTimeRange] = useState('month');
  const [selectedDomain, setSelectedDomain] = useState('all');
  const [selectedAgency, setSelectedAgency] = useState('all');

  const { documents: articles, loading: articlesLoading } = useFirestore('articles');
  const { documents: domains } = useFirestore('domains');
  const { documents: agencies } = useFirestore('agencies');

  const filteredArticles = useMemo(() => {
    return articles.filter(article => {
      const matchesDomain = selectedDomain === 'all' || article.domain_id === selectedDomain;
      const matchesAgency = selectedAgency === 'all' || article.agency_id === selectedAgency;
      const hasPrice = article.price && article.price > 0;
      return matchesDomain && matchesAgency && hasPrice;
    });
  }, [articles, selectedDomain, selectedAgency]);

  const earningsData = useMemo(() => {
    const now = new Date();
    const data = [];

    if (timeRange === 'month') {
      // Last 30 days
      for (let i = 29; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        
        const dayEarnings = filteredArticles
          .filter(article => {
            const articleDate = article.published_at || article.created_at;
            return articleDate && new Date(articleDate).toISOString().split('T')[0] === dateStr;
          })
          .reduce((sum, article) => sum + (article.price || 0), 0);

        data.push({
          date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          earnings: dayEarnings
        });
      }
    } else if (timeRange === 'year') {
      // Last 12 months
      for (let i = 11; i >= 0; i--) {
        const date = new Date(now);
        date.setMonth(date.getMonth() - i);
        const year = date.getFullYear();
        const month = date.getMonth();
        
        const monthEarnings = filteredArticles
          .filter(article => {
            const articleDate = article.published_at || article.created_at;
            return articleDate && 
                   new Date(articleDate).getFullYear() === year && 
                   new Date(articleDate).getMonth() === month;
          })
          .reduce((sum, article) => sum + (article.price || 0), 0);

        data.push({
          date: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
          earnings: monthEarnings
        });
      }
    } else if (timeRange === 'week') {
      // Last 7 days
      for (let i = 6; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        
        const dayEarnings = filteredArticles
          .filter(article => {
            const articleDate = article.published_at || article.created_at;
            return articleDate && new Date(articleDate).toISOString().split('T')[0] === dateStr;
          })
          .reduce((sum, article) => sum + (article.price || 0), 0);

        data.push({
          date: date.toLocaleDateString('en-US', { weekday: 'short' }),
          earnings: dayEarnings
        });
      }
    }

    return data;
  }, [filteredArticles, timeRange]);

  const totalEarnings = useMemo(() => {
    return filteredArticles.reduce((sum, article) => sum + (article.price || 0), 0);
  }, [filteredArticles]);

  const todayEarnings = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return filteredArticles
      .filter(article => {
        const articleDate = article.published_at || article.created_at;
        return articleDate && new Date(articleDate).toISOString().split('T')[0] === today;
      })
      .reduce((sum, article) => sum + (article.price || 0), 0);
  }, [filteredArticles]);

  const thisMonthEarnings = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    return filteredArticles
      .filter(article => {
        const articleDate = article.published_at || article.created_at;
        return articleDate && 
               new Date(articleDate).getMonth() === currentMonth && 
               new Date(articleDate).getFullYear() === currentYear;
      })
      .reduce((sum, article) => sum + (article.price || 0), 0);
  }, [filteredArticles]);

  const avgEarningsPerArticle = useMemo(() => {
    return filteredArticles.length > 0 ? totalEarnings / filteredArticles.length : 0;
  }, [totalEarnings, filteredArticles.length]);

  const getDomainName = (domainId) => {
    const domain = domains.find(d => d.id === domainId);
    return domain ? domain.name : 'Unknown Domain';
  };

  const getAgencyName = (agencyId) => {
    const agency = agencies.find(a => a.id === agencyId);
    return agency ? agency.name : 'Unknown Agency';
  };

  if (articlesLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading earnings data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Earnings Analytics</h1>
          <p className="text-gray-600">Track your article earnings and performance</p>
        </div>
        <div className="flex space-x-2 mt-4 sm:mt-0">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="year">Last 12 Months</option>
          </select>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Total Earnings</p>
              <p className="text-3xl font-bold text-gray-900">{totalEarnings.toFixed(2)} LEI</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-primary-500 flex items-center justify-center">
              <SafeIcon icon={FiCoins} className="w-6 h-6 text-white" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Today's Earnings</p>
              <p className="text-3xl font-bold text-gray-900">{todayEarnings.toFixed(2)} LEI</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-success-500 flex items-center justify-center">
              <SafeIcon icon={FiCalendar} className="w-6 h-6 text-white" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">This Month</p>
              <p className="text-3xl font-bold text-gray-900">{thisMonthEarnings.toFixed(2)} LEI</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-warning-500 flex items-center justify-center">
              <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-white" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Avg per Article</p>
              <p className="text-3xl font-bold text-gray-900">{avgEarningsPerArticle.toFixed(2)} LEI</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-purple-500 flex items-center justify-center">
              <SafeIcon icon={FiFileText} className="w-6 h-6 text-white" />
            </div>
          </div>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="relative">
          <SafeIcon icon={FiFilter} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <select
            value={selectedDomain}
            onChange={(e) => setSelectedDomain(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all">All Domains</option>
            {domains.map(domain => (
              <option key={domain.id} value={domain.id}>
                {domain.name}
              </option>
            ))}
          </select>
        </div>

        <select
          value={selectedAgency}
          onChange={(e) => setSelectedAgency(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="all">All Agencies</option>
          {agencies.map(agency => (
            <option key={agency.id} value={agency.id}>
              {agency.name}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <EarningsChart
          data={earningsData}
          title={`Earnings - ${timeRange === 'week' ? 'Last 7 Days' : timeRange === 'month' ? 'Last 30 Days' : 'Last 12 Months'}`}
          type="line"
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent High-Value Articles</h3>
          <div className="space-y-3">
            {filteredArticles
              .sort((a, b) => (b.price || 0) - (a.price || 0))
              .slice(0, 5)
              .map((article, index) => (
                <div key={article.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900 truncate">{article.title}</div>
                    <div className="text-xs text-gray-500">
                      {getAgencyName(article.agency_id)} • {getDomainName(article.domain_id)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-success-600">{(article.price || 0).toFixed(2)} LEI</div>
                    <div className="text-xs text-gray-500">
                      {new Date(article.published_at || article.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Domain Performance</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Domain
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Articles
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Earnings
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg per Article
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {domains.map((domain) => {
                const domainArticles = filteredArticles.filter(article => article.domain_id === domain.id);
                const domainEarnings = domainArticles.reduce((sum, article) => sum + (article.price || 0), 0);
                const avgPerArticle = domainArticles.length > 0 ? domainEarnings / domainArticles.length : 0;

                return (
                  <tr key={domain.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{domain.name}</div>
                      <div className="text-sm text-gray-500">{domain.url}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{domainArticles.length}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-success-600">{domainEarnings.toFixed(2)} LEI</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{avgPerArticle.toFixed(2)} LEI</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default Earnings;